import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getDefaultPathForRole, mustForcePasswordChange, writeSessionFromApi } from "../../lib/authSession";
import { loginRequest } from "../../lib/api";

export default function LoginPage() {
  const navigate = useNavigate();

  const [telefon, setTelefon] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError("");

    const rawTelefon = telefon.trim();
    const rawPassword = password.trim();

    if (!rawTelefon || !rawPassword) {
      setError("Lütfen tüm alanları doldurun.");
      return;
    }

    setLoading(true);

    try {
      const result = await loginRequest(rawTelefon, rawPassword);
      writeSessionFromApi(result.user, result.access_token);

      if (mustForcePasswordChange({
        rol: result.user.rol,
        forcePasswordChange: result.user.force_password_change,
        smsOperationalOptIn: result.user.sms_operational_opt_in ?? null,
        smsMarketingOptIn: result.user.sms_marketing_opt_in ?? null,
      })) {
        navigate("/sifre-degistir", { replace: true });
      } else {
        navigate(getDefaultPathForRole(result.user.rol), { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Giriş başarısız.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-farm-cream flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm mb-5">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-farm-muted hover:text-farm-brown font-body text-xs transition-colors cursor-pointer"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-left-line text-sm" />
          </div>
          Ana sayfaya dön
        </Link>
      </div>

      <div className="w-full max-w-sm bg-farm-warm border border-farm-border rounded-farm shadow-sm">
        <div className="relative rounded-t-farm overflow-hidden h-20">
          <img
            src="https://readdy.ai/api/search-image?query=soft%20watercolor%20illustration%20wide%20farm%20landscape%20strip%20with%20golden%20sunflowers%20green%20fields%20rustic%20barn%20soft%20blue%20sky%20warm%20golden%20light%20dreamy%20illustrated%20style%20no%20text%20minimal%20decorative%20header%20band&width=800&height=160&seq=evren-login-header-v1&orientation=landscape"
            alt="Evren Çiftliği"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-farm-brown/30 to-farm-brown/55" />
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 flex items-center justify-center rounded-full overflow-hidden bg-white/20 backdrop-blur-sm">
                <img
                  src="https://public.readdy.ai/ai/img_res/73cf5855-ea32-42f4-956b-881be407de25.png"
                  alt="Evren Çiftliği logo"
                  className="w-6 h-6 object-contain"
                />
              </div>
              <span className="font-heading font-semibold text-white text-base drop-shadow">
                Evren Çiftliği
              </span>
            </div>
          </div>
        </div>

        <div className="p-5">
          <div className="mb-5">
            <h1 className="font-heading font-bold text-lg text-farm-brown mb-1">
              Giriş Yap
            </h1>
            <p className="font-body text-xs text-farm-muted leading-relaxed">
              Telefon numaranız veya kullanıcı adınız ve şifreniz ile giriş yapın.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-3" noValidate>
            <div className="flex flex-col gap-1">
              <label
                htmlFor="telefon"
                className="font-body text-xs font-semibold text-farm-brown"
              >
                Telefon
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-farm-muted">
                  <i className="ri-smartphone-line text-sm" />
                </div>
                <input
                  id="telefon"
                  type="text"
                  name="telefon"
                  value={telefon}
                  onChange={(e) => setTelefon(e.target.value)}
                  placeholder="0532 123 45 67 veya sira.operator"
                  autoComplete="username"
                  className="w-full pl-9 pr-3 py-2.5 rounded-md border border-farm-border bg-farm-cream font-body text-sm text-farm-brown placeholder:text-farm-muted/60 focus:outline-none focus:border-farm-earth focus:ring-1 focus:ring-farm-earth/30 transition-colors"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label
                htmlFor="password"
                className="font-body text-xs font-semibold text-farm-brown"
              >
                Şifre
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-farm-muted">
                  <i className="ri-lock-2-line text-sm" />
                </div>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="w-full pl-9 pr-9 py-2.5 rounded-md border border-farm-border bg-farm-cream font-body text-sm text-farm-brown placeholder:text-farm-muted/60 focus:outline-none focus:border-farm-earth focus:ring-1 focus:ring-farm-earth/30 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-farm-muted hover:text-farm-brown transition-colors cursor-pointer"
                  aria-label={showPassword ? "Şifreyi gizle" : "Şifreyi göster"}
                >
                  <i
                    className={`${
                      showPassword ? "ri-eye-off-line" : "ri-eye-line"
                    } text-sm`}
                  />
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-red-50 border border-red-100">
                <div className="w-4 h-4 flex items-center justify-center text-red-400 flex-shrink-0">
                  <i className="ri-error-warning-line text-sm" />
                </div>
                <p className="font-body text-xs text-red-600">{error}</p>
              </div>
            )}

            <div className="rounded-md border border-farm-border bg-farm-cream px-3 py-2 text-[10px] text-farm-muted leading-relaxed">
              İlk girişte şifre değişimi ve müşteri SMS tercihleri tamamlanmadan
              panele giriş yapılmaz.
            </div>

            <button
              type="submit"
              disabled={loading}
              className="h-10 rounded-md bg-farm-earth hover:bg-farm-brown text-white font-semibold text-sm transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? "Kontrol ediliyor..." : "Giriş Yap"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}